"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, CircleX as XCircle, Zap, TrendingUp, Clock, Target, DollarSign, ChevronDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
const comparisonBad = [{
  id: 1,
  text: "Leads slip through cracks with no structured follow-up"
}, {
  id: 2,
  text: "Slow or non-existent response times kill initial buyer interest"
}, {
  id: 3,
  text: "Zero visibility on which marketing channels actually produce clients"
}, {
  id: 4,
  text: "Paying for traffic that leaves your website without taking action"
}];
const comparisonGood = [{
  id: 1,
  text: "Every visitor receives a clear, frictionless next step"
}, {
  id: 2,
  text: "Instant AI receptionist response and multi-channel lead engagement"
}, {
  id: 3,
  text: "Focus on booked appointments directly on your calendar"
}, {
  id: 4,
  text: "Full attribution tracking from first click to closed deal"
}];
const impactStats = [{
  id: 1,
  val: "3.5x",
  label: "Booked Calls",
  subtext: "Average increase in calendar bookings",
  icon: Target
}, {
  id: 2,
  val: "< 60s",
  label: "Response Time",
  subtext: "Instant automated lead engagement",
  icon: Clock
}, {
  id: 3,
  val: "+42%",
  label: "Close Rate",
  subtext: "Higher intent qualified prospect traffic",
  icon: TrendingUp
}, {
  id: 4,
  val: "-35%",
  label: "Cost Per Client",
  subtext: "Reduced wasted ad and search spend",
  icon: DollarSign
}];
const conversionStages = [{
  number: "01",
  title: "Frictionless First Contact",
  tagline: "24/7 Instant AI Qualification & Chat",
  description: "Capture high-intent visitors before they bounce. Our AI receptionist engages prospects instantly, answers complex business questions, and qualifies lead intent in real time.",
  bullets: ["Sub-60 second automated response time across all channels", "Custom lead qualification and decision-tree logic", "Direct calendar integration for instant booking"],
  metric: "< 60s Lead Response Time"
}, {
  number: "02",
  title: "High-Converting Page Architecture",
  tagline: "Intent-Driven Conversion Design",
  description: "Transform passive site traffic into committed appointments through high-contrast mobile layouts, persuasive headline frameworks, and strategic trust positioning.",
  bullets: ["Ultra-fast loading speed and clean user experience", "Embedded review proof and dynamic trust badges", "Friction-free booking forms and primary action triggers"],
  metric: "3.5x Increase in Booked Calls"
}, {
  number: "03",
  title: "Automated Multi-Touch Follow-Up",
  tagline: "Zero Opportunity Leakage",
  description: "Never lose another lead to delayed communication. Intelligent SMS and email sequences automatically follow up until the prospect schedules a consultation.",
  bullets: ["Instant SMS confirmation & automated call reminders", "Multi-channel drip campaigns for hesitant prospects", "Automated no-show recovery and re-engagement workflows"],
  metric: "+42% Higher Call Attendance Rate"
}, {
  number: "04",
  title: "Centralized Lead Pipeline & CRM Sync",
  tagline: "Full Revenue Pipeline Control",
  description: "Maintain 100% visibility over every active deal. All inbound leads, chat transcripts, and booked appointments feed directly into a clean, organized command center.",
  bullets: ["Unified contact history and communication logs", "Automated pipeline stage updates and lead scoring", "Real-time notifications for high-value sales opportunities"],
  metric: "100% Pipeline Visibility"
}, {
  number: "05",
  title: "Closed-Loop Attribution & Analytics",
  tagline: "Data-Driven Marketing Scaling",
  description: "Eliminate marketing guesswork. Track every closed deal back to its original source—whether AI search, local map pack, or targeted campaigns—so you invest with certainty.",
  bullets: ["End-to-end first-touch to closed-won revenue tracking", "Transparent monthly reporting on return on ad spend", "Continuous conversion rate optimization and A/B testing"],
  metric: "-35% Cost Per Client Acquisition"
}];
export default function Conversion() {
  const shouldReduceMotion = useReducedMotion();
  const [expandedStage, setExpandedStage] = useState<number | null>(0);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  return <section id="conversionecosystemcta" data-nav="dark" className="bg-background text-foreground py-24 md:py-36 relative overflow-hidden">
      <div aria-hidden="true" className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[600px] bg-[var(--accent-deep-red)]/10 blur-[140px] pointer-events-none rounded-lg" />

      <div className="container mx-auto px-4 md:px-8 relative z-10 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <Badge variant="outline" className="mb-4 border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)] font-mono text-xs tracking-wider uppercase px-3 py-1">
            WHY IT MATTERS
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
            Predictable Growth Starts With turning Traffic Into{" "}
            <span className="font-serif italic text-[var(--accent-deep-red)]">Revenue</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A lead generation system without modern AI conversion tools wastes marketing spend. ClearLine builds an integrated engine that captures every lead, automates follow-ups, and books qualified calls straight into your calendar.
          </p>
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-20">
          <motion.div variants={itemVariants}>
            <Card className="h-full bg-card/40 border-border/60 backdrop-blur-xs flex flex-col justify-between">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="secondary" className="bg-muted text-muted-foreground">
                    Traditional Approach
                  </Badge>
                  <XCircle className="size-5 text-muted-foreground" />
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                  Traffic Without a System
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  How most service businesses bleed potential revenue every day.
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-4">
                <ul className="space-y-4">
                  {comparisonBad.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3">
                      <XCircle className="size-5 text-muted-foreground shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-muted-foreground">{item.text}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="h-full bg-card border-[var(--accent-deep-red)]/40 shadow-xl shadow-[var(--accent-deep-red)]/5 relative overflow-hidden flex flex-col justify-between">
              <div aria-hidden="true" className="absolute top-0 right-0 size-32 bg-[var(--accent-deep-red)]/10 blur-2xl pointer-events-none" />
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="default" className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]">
                    ClearLine System
                  </Badge>
                  <Zap className="size-5 text-[var(--accent-deep-red)]" />
                </div>
                <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                  The ClearLine Conversion Engine
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Automated visibility, instant response, and booked appointments.
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-4">
                <ul className="space-y-4">
                  {comparisonGood.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="size-5 text-[var(--accent-deep-red)] shrink-0 mt-0.5" />
                      <span className="text-sm sm:text-base text-foreground font-medium">{item.text}</span>
                    </li>)}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants} className="grid grid-cols-2 md:grid-cols-4 gap-6 p-6 sm:p-8 rounded-xl bg-card/60 border border-border/50 mb-28">
          {impactStats.map((stat, i) => {
          const Icon = stat.icon;
          return <motion.div key={stat.id} data-index={i} variants={itemVariants} className="text-center p-4">
                <div className="inline-flex items-center justify-center size-10 rounded-lg bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] mb-3">
                  <Icon className="size-5" />
                </div>
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground tracking-tight">
                  {stat.val}
                </div>
                <div className="text-sm font-semibold text-foreground mt-1">{stat.label}</div>
                <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{stat.subtext}</p>
              </motion.div>;
        })}
        </motion.div>

        <div className="mb-28 max-w-4xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="outline" className="mb-3 border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)] font-mono text-xs tracking-wider uppercase px-3 py-1">
              THE CONVERSION PROCESS
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
              How We Turn Traffic Into Booked Revenue
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground mt-2">
              A 5-stage conversion framework designed to eliminate friction and maximize appointment volume.
            </p>
          </div>

          <div className="border-t border-border/50 divide-y divide-border/40">
            {conversionStages.map((stage, i) => {
            const isExpanded = expandedStage === i;
            return <div key={stage.number} data-index={i} className="py-5 sm:py-6 transition-colors">
                  <Button variant="ghost" onClick={() => setExpandedStage(isExpanded ? null : i)} className="w-full h-auto p-0 flex items-center justify-between text-left group gap-4 cursor-pointer focus:outline-none hover:bg-transparent hover:text-foreground whitespace-normal rounded-lg" aria-expanded={isExpanded}>
                    <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                      <span className={cn("font-mono text-lg sm:text-2xl font-bold transition-colors shrink-0", isExpanded ? "text-[var(--accent-deep-red)]" : "text-muted-foreground group-hover:text-foreground")}>
                        {stage.number}
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-base sm:text-xl font-bold text-foreground group-hover:text-[var(--accent-deep-red)] transition-colors truncate">
                          {stage.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate mt-0.5">
                          {stage.tagline}
                        </p>
                      </div>
                    </div>
                    <div className={cn("size-8 rounded-lg border border-border/60 flex items-center justify-center shrink-0 transition-all", isExpanded ? "bg-[var(--accent-deep-red)]/10 border-[var(--accent-deep-red)]/40 text-[var(--accent-deep-red)]" : "text-muted-foreground group-hover:border-foreground/30")}>
                      <ChevronDown className={cn("size-4 transition-transform duration-300", isExpanded && "rotate-180")} />
                    </div>
                  </Button>

                  <AnimatePresence initial={false}>
                    {isExpanded && <motion.div key="content" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: "auto"
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3,
                  ease: "easeInOut"
                }} className="overflow-hidden">
                        <div className="pt-4 sm:pt-6 pl-9 sm:pl-12 space-y-4">
                          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                            {stage.description}
                          </p>
                          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                            {stage.bullets.map((bullet, bIdx) => <li key={bIdx} data-index={bIdx} className="flex items-start gap-2 text-xs sm:text-sm text-foreground">
                                <CheckCircle2 className="size-4 text-[var(--accent-deep-red)] shrink-0 mt-0.5" />
                                <span>{bullet}</span>
                              </li>)}
                          </ul>
                          <div className="pt-2">
                            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] text-xs sm:text-sm font-semibold border border-[var(--accent-deep-red)]/20">
                              <TrendingUp className="size-3.5 shrink-0" />
                              <span>{stage.metric}</span>
                            </span>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }}>
          <Card className="bg-card border-[var(--accent-deep-red)]/40 p-8 sm:p-12 md:p-16 text-center relative overflow-hidden shadow-2xl">
            <div aria-hidden="true" className="absolute -top-24 left-1/2 -translate-x-1/2 size-96 bg-[var(--accent-deep-red)]/15 blur-3xl pointer-events-none rounded-lg" />
            
            <div className="relative z-10 max-w-3xl mx-auto">
              <Badge variant="outline" className="mb-4 border-[var(--accent-deep-red)]/50 text-[var(--accent-deep-red)] uppercase tracking-wider text-xs">
                READY FOR PREDICTABLE LEADS?
              </Badge>
              <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground tracking-tight leading-tight">
                Build a Growth System That Converts Visitors Into Clients
              </h3>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Get a complete audit of your online visibility or speak directly with our strategy team to see how ClearLine can transform your sales pipeline.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90 w-full sm:w-auto font-medium px-8 py-6 text-base">
                  <Link to="/PricingPlansPage"><span>Get started n</span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-2 size-5" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent text-foreground w-full sm:w-auto font-medium px-8 py-6 text-base">
                  <Link to="/VisibilityCheckupPage">
                    <span>Free AI Visibility Checkup</span>
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}